import type { TakiSdk } from '@taki/sdk';
import { confirmationDeBouton } from '@taki/data-model';
import type { ConfirmationAssemblee } from '@taki/ui';

/**
 * LE PORT DES SMART BOUTONS FLUX (`docs/socle/smart-boutons.md`).
 *
 * UN BOUTON A ACTION FLUX NE NOMME AUCUN FLUX : c'est le flux qui s'abonne a lui
 * (declencheur « Bouton »). L'ecran n'a donc que deux questions a poser au
 * serveur, et ce port les porte, injectable pour les tests :
 *
 * - `branchements` : quels flux actifs sont abonnes a quels boutons de l'app ?
 *   Un bouton sans flux est grise (« Aucun flux actif sur ce bouton ») ;
 * - `declencher` : le clic. Le serveur relit la declaration et la fiche, rejoue
 *   la condition, lance les flux ; un refus arrive en ERREUR, au message lisible.
 *
 * L'HOTE BRANCHE LE PORT PAR DEFAUT sur `sdk.flux` (`portDeBoutonsFluxDuSdk`) ;
 * un test ou un autre hote peut en poser un autre dans `AppRuntimeProvider`
 * (`boutonsFlux`).
 */

/** Un flux actif abonne a un bouton. */
export interface FluxBranche {
  fluxId: string;
  nom: string;
}

/** Les branchements d'une app, par `clefDeBouton` (`app/porte/bouton`). */
export type BranchementsDeBoutons = Record<string, FluxBranche[]>;

/** Ce que l'ecran demande au serveur au clic. */
export interface EntreeDeBouton {
  appId: string;
  porteId: string;
  boutonId: string;
  /** La fiche ouverte (bouton de niveau fiche) ; absente d'un bouton de collection. */
  ficheId?: string;
  /** La personne a vu la liste des sorties hors de l'equipe et a dit oui. */
  confirme?: boolean;
  /** Tous les controles du serveur, les sorties a montrer (possiblement aucune), rien de lance : la fenetre unique d'un bouton qui declare une confirmation. */
  apercu?: boolean;
}

/** Une sortie hors de l'equipe qu'un flux branche ferait (e-mail, WhatsApp, appel http, connecteur). */
export interface SortieDeBouton {
  fluxId: string;
  fluxNom: string;
  libelle: string;
}

export type StatutDExecutionDeBouton = 'reussi' | 'echec' | 'en_pause' | 'en_cours' | 'arrete';

/** Une execution lancee par le clic. */
export interface ExecutionDeBouton {
  fluxId: string;
  fluxNom: string;
  executionId: string;
  statut: StatutDExecutionDeBouton;
  erreur?: string;
}

/**
 * La reponse du serveur. `confirmation` : rien n'est parti, des sorties hors de
 * l'equipe attendent un oui. `lance` : les flux ont tourne.
 */
export type ReponseDeBouton =
  | { statut: 'confirmation'; sorties: SortieDeBouton[] }
  | { statut: 'lance'; executions: ExecutionDeBouton[] };

export interface PortDeBoutonsFlux {
  branchements(appId: string): Promise<BranchementsDeBoutons>;
  declencher(entree: EntreeDeBouton): Promise<ReponseDeBouton>;
}

/** Les mots de l'etat grise d'un bouton flux, ecrits une fois : les rangees et les tests les citent. */
export const RAISON_AUCUN_FLUX = 'Aucun flux actif sur ce bouton';
export const RAISON_VERIFICATION = 'Vérification en cours';

/** La clef d'un bouton dans les branchements : `app/porte/bouton`. */
export const clefDeBouton = (appId: string, porteId: string, boutonId: string): string =>
  `${appId}/${porteId}/${boutonId}`;

/**
 * Le PORT PAR DEFAUT, branche sur les deux methodes du client Flux du SDK
 * (`sdk.flux.listerBranchementsDeBoutons`, `sdk.flux.declencherBouton`). `null`
 * quand le SDK ne les porte pas (un SDK plus ancien, un faux SDK de test) : les
 * boutons flux sont alors grises, sans rien casser.
 */
export function portDeBoutonsFluxDuSdk(sdk: TakiSdk): PortDeBoutonsFlux | null {
  const flux = (sdk as unknown as {
    flux?: {
      listerBranchementsDeBoutons?: (appId?: string) => Promise<BranchementsDeBoutons>;
      declencherBouton?: (entree: EntreeDeBouton) => Promise<ReponseDeBouton>;
    };
  }).flux;
  const lister = flux?.listerBranchementsDeBoutons;
  const declencher = flux?.declencherBouton;
  if (flux === undefined || lister === undefined || declencher === undefined) return null;
  return {
    branchements: (appId) => lister.call(flux, appId),
    declencher: (entree) => declencher.call(flux, entree),
  };
}

/* ------------------------------------------------------------------ */
/* Les mots : ce que la personne lit                                   */
/* ------------------------------------------------------------------ */

/**
 * L'avis final d'un lancement, rendu par la PASTILLE de la rangee 3 (plus aucune
 * fenetre, plus de lien vers le suivi : l'app Flux n'est pas chez tout le monde).
 * `fait` : tout a reussi ; `neutre` : en cours, en pause ou arrete ; `echec` : au
 * moins une execution en echec ; `refus` : le serveur a refuse le lancement.
 */
export interface AvisDeBouton {
  ton: 'fait' | 'neutre' | 'echec' | 'refus';
  /** Ce que la personne lit : « Action exécutée », « Action en échec », « Action en cours », « Action refusée : ... ». */
  texte: string;
  /** L'erreur de l'execution en echec, au survol de la pastille. */
  detail?: string;
}

/**
 * L'AVIS D'UN LANCEMENT, en trois mots possibles, sans le nom du bouton ni le
 * compte des flux : « Action exécutée » quand tout a réussi, « Action en
 * échec » (l'erreur au survol) quand une exécution a échoué, « Action en cours »
 * quand une attend ou tourne encore, « Action arrêtée » quand une a été arrêtée.
 */
export function avisDeLancement(executions: readonly ExecutionDeBouton[]): AvisDeBouton {
  if (executions.length === 0) return { ton: 'refus', texte: 'Action refusée : aucun flux lancé' };
  const enEchec = executions.find((execution) => execution.statut === 'echec');
  if (enEchec !== undefined) {
    return { ton: 'echec', texte: 'Action en échec', ...(enEchec.erreur === undefined ? {} : { detail: enEchec.erreur }) };
  }
  if (executions.every((execution) => execution.statut === 'reussi')) return { ton: 'fait', texte: 'Action exécutée' };
  if (executions.some((execution) => execution.statut === 'en_pause' || execution.statut === 'en_cours')) {
    return { ton: 'neutre', texte: 'Action en cours' };
  }
  return { ton: 'neutre', texte: 'Action arrêtée' };
}

/** Le refus du serveur (une erreur du client Flux), au message lisible : « Action refusée : <raison> ». */
export function avisDeRefus(cause: unknown): AvisDeBouton {
  const message = cause instanceof Error && cause.message !== '' ? cause.message : String(cause);
  return { ton: 'refus', texte: `Action refusée : ${message}` };
}

/**
 * LA FICHE (OU LA PORTE) SUIT CE QUE LE FLUX ECRIT. Un flux lance par un bouton
 * ecrit la fiche COTE SERVEUR, comme le declencheur d'un champ automatique : une
 * porte qui declare au moins un smart bouton flux de niveau FICHE s'abonne donc a
 * la fiche ouverte (`observe`), au meme titre qu'un jeu a champ automatique.
 */
export function suitLeFluxDeFiche(actionsDeFiche: readonly { flux?: boolean | undefined }[]): boolean {
  return actionsDeFiche.some((action) => action.flux === true);
}

/**
 * LA FENETRE UNIQUE D'UN BOUTON FLUX. Un bouton qui declare une confirmation ne
 * montre PLUS la sienne au clic, puis une seconde pour les envois hors de
 * l'equipe : il demande au serveur l'apercu (`apercu: true`, rien ne part), et
 * UNE fenetre dit ce qui part. Dans l'ordre : le titre declare (sinon le libelle
 * du bouton), le corps declare SEULEMENT s'il n'est pas le corps par defaut que
 * pose « Confirmation requise » (`confirmationDeBouton` : une phrase qui ne dit
 * rien), puis, s'il y a des sorties, « Ce bouton envoie hors de l'équipe : » et
 * une ligne par sortie (le serveur les a resolues : « E-mail à luc@exemple.lu »),
 * sans le nom du flux quand un seul flux envoie. Avec des sorties, les verbes
 * sont « Annuler » et « Envoyer » ; sans, ceux de la confirmation declaree.
 */
export function fenetreDesSorties(
  libelle: string,
  sorties: readonly SortieDeBouton[],
  declaree?: ConfirmationAssemblee,
): Omit<ConfirmationAssemblee, 'body'> & { body: string[] } {
  const parFlux = new Set(sorties.map((sortie) => sortie.fluxId)).size > 1;
  const corpsParDefaut = declaree !== undefined && [confirmationDeBouton(libelle, 'fiche'), confirmationDeBouton(libelle, 'collection')]
    .some((defaut) => defaut.corps === declaree.body);
  return {
    tone: declaree?.tone ?? 'neutral',
    title: declaree?.title ?? `${libelle} ?`,
    body: [
      ...(declaree === undefined || (corpsParDefaut && sorties.length > 0) || declaree.body === '' ? [] : [declaree.body]),
      ...(sorties.length === 0
        ? []
        : ['Ce bouton envoie hors de l\'équipe :', ...sorties.map((sortie) => (parFlux ? `${sortie.fluxNom} : ${sortie.libelle}` : sortie.libelle))]),
    ],
    confirmLabel: sorties.length === 0 && declaree !== undefined ? declaree.confirmLabel : 'Envoyer',
    ...(sorties.length > 0 ? { cancelLabel: 'Annuler' } : declaree?.cancelLabel === undefined ? {} : { cancelLabel: declaree.cancelLabel }),
  };
}

/** L'evenement que le panneau des pieces jointes emet quand la liste d'une fiche a change. */
export const EVENEMENT_PIECES_JOINTES = 'taki:pieces-jointes';

/** Dit aux boutons qui lisent `socle:piecesJointes` de relire la liste. */
export function signalerLesPiecesJointes(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENEMENT_PIECES_JOINTES));
}
